import { AbortError } from '@frontend/controllers';
import { Heap } from '@frontend/datatypes';
import { WaitUntilSetController } from '@frontend/utils';

import type { RendererState } from './renderer';

interface LoadableBase {
  //Note: id can be falsey here, but in LoadingPriorityQueue, loadables with falsey ids are never added
  //* loadables with falsey values (such as stock built on fly) should be loaded syncronously outside of LoadingPriorityQueue
  readonly id?: string | number;
  readonly isLoaded: boolean;
}

export interface AsyncLoadable extends LoadableBase {
  readonly isPaused: boolean;
  readonly isLoading: boolean;
  loadAsync(lastPause?: number): Promise<number>; //returns lastPause
  pause(): void;
  resume(): void;
}

export interface SyncLoadable extends LoadableBase {
  load(): void;
}

const isAsyncLoadableTask = (
  item: unknown,
): item is TaskItem<AsyncLoadable, number> =>
  !!item &&
  (item as TaskItem<AsyncLoadable, number>).loadable &&
  typeof (item as TaskItem<AsyncLoadable, number>).loadable.loadAsync ===
    'function';

const isLoadableTask = (item: unknown): item is AnyTaskItem =>
  !!item &&
  (item as AnyTaskItem).loadable &&
  (typeof (item as TaskItem<AsyncLoadable, number>).loadable.loadAsync ===
    'function' ||
    typeof (item as TaskItem<SyncLoadable, number>).loadable.load ===
      'function');

const isLoadable = (item: unknown): item is SyncLoadable | AsyncLoadable =>
  !!item &&
  (typeof (item as AsyncLoadable).loadAsync === 'function' ||
    typeof (item as SyncLoadable).load === 'function');

type HasPriority<P extends number> = {
  priority: P;
};

//Loading task item
type TaskItem<
  T extends AsyncLoadable | SyncLoadable,
  P extends number,
> = HasPriority<P> & {
  loadable: T;
};

type AnyTaskItem =
  | TaskItem<AsyncLoadable, number>
  | TaskItem<SyncLoadable, number>;
type TaskId = Exclude<(AsyncLoadable | SyncLoadable)['id'], undefined>;

const HeapCompare = <P extends number>(
  a: HasPriority<P>,
  b: HasPriority<P>,
): number => a.priority - b.priority;

export class LoadingPriorityQueue<
  T extends SyncLoadable | AsyncLoadable,
  S extends RendererState,
  P extends number,
> {
  //Notes:
  //* taskHeap maintains TaskItems in priority.
  //  * TaskItems in `this.toRemove` should be skipped/ignored in TaskHeap
  //    Recall the taskHeap only exposes top item. If a task's priority changes and its not at the top of the queue,
  //    then we want to avoid searching for it by popping until we find it and pushing the other tasks back. Instead
  //    we just mark it for removal and remove it when we get to it in future.
  //* taskHeap and tasks only collect TaskItem that have an `id` (falsey ids (undefined, null, 0, '') are not included);
  #taskHeap = new Heap<TaskItem<T, P>>(HeapCompare);
  #tasks = new Map<TaskId, TaskItem<T, P>>();
  #toRemove = new Set<TaskItem<T, P>>();
  #taskHeapDrained?: WaitUntilSetController<number>; //Finished draining taskHeap
  #lastPause?: number = undefined;
  #state?: S;

  constructor(
    private afterEach: (loaded: T) => void,
    private afterEachPriorityLevel: (nextState: S, priorityLevel?: P) => void,
    private onFail: (loadable: T) => void,
    private signal: AbortSignal,
  ) {}

  public get size(): number {
    return this.#tasks.size;
  }

  public get lastPause(): number | undefined {
    return this.#lastPause;
  }

  public get isRunning(): boolean {
    return !!this.#taskHeapDrained;
  }

  public get currentlyLoadingId(): string | number | undefined {
    return this.peek()?.loadable?.id;
  }

  public has(taskOrLoadableOrId: TaskItem<T, P> | T | T['id']): boolean {
    const id: string | number | undefined =
      typeof taskOrLoadableOrId === 'string' ||
      typeof taskOrLoadableOrId === 'number'
        ? taskOrLoadableOrId
        : isLoadable(taskOrLoadableOrId)
        ? taskOrLoadableOrId.id
        : isLoadableTask(taskOrLoadableOrId)
        ? taskOrLoadableOrId.loadable.id
        : undefined;
    //* Recall tasks with falsey loadable ids are (currently) never added to LoadingPriorityQueue
    return !!id && this.#tasks.has(id);
  }

  //tasks: tasks in queue (sorted by priority)
  //* useful for inspecting queue when debugging (could be more performant? (if ever needed for production) but it works fast enough for dev)
  public get tasks(): TaskItem<T, P>[] {
    return this.#taskHeap
      .toArray()
      .filter(
        (task) => task && !task.loadable.isLoaded && !this.#toRemove.has(task),
      );
  }

  //tasks: tasks in queue (not sorted)
  //* useful for inspecting queue when debugging (could be more performant? (if ever needed for production) but it works fast enough for dev)
  public get unsortedTasks(): TaskItem<T, P>[] {
    return Array.from(this.#tasks.values());
  }

  public clear(): void {
    while (this.#taskHeap.size > 0) {
      const popped = this.#taskHeap.pop();
      if (isAsyncLoadableTask(popped) && popped.loadable.isLoading) {
        popped.loadable.pause();
      }
    }
    this.#toRemove.clear();
    this.#tasks.clear();

    this.#taskHeapDrained?.set(this.#lastPause || performance.now());
    this.#taskHeapDrained = undefined;
  }

  public updateState(nextState: S) {
    this.#state = nextState;
  }

  public removeTasksFor(loadable: T): void {
    for (const task of this.#tasks.values()) {
      if (task.loadable === loadable) {
        this.remove(task);
      }
    }
  }

  //updateAndStart():
  //* Adds items to the priority queue that are not loaded. If item exists, it is replaced if the priority has changed
  //* After adding items to the queue, the work to process the queue starts (only starts if not already processing)
  //  * It is fine to call updateAndStart() to add more work to the queue while the queue is being processed
  //  * If the queue is empty, more items are added to queue, and processing of the queue will re-start
  //* Note, when an assemblyInstance is loaded, it will load its descendants in appropriate order and this often
  //  results in other items in the queue to become `done`. So the queue's population can drop in chunks.
  //* Returns promise that resolves when queue is drained
  public updateAndStart(
    tasks: Iterable<TaskItem<T, P>>,
    nextState: S,
    lastPause: number = performance.now(),
  ): Promise<number> {
    if (this.signal?.aborted) {
      throw new AbortError(
        'Loading by data aborted because an abort signal was received',
      );
    }
    this.#state = nextState;
    for (const task of tasks) {
      const id = task.loadable.id;
      if (id) {
        const existingTask = this.#tasks.get(id);
        if (existingTask) {
          if (task.priority !== existingTask.priority) {
            //Replace existingTask with task because priority changed
            this.remove(existingTask);
            if (!task.loadable.isLoaded) {
              this.#taskHeap.push(task);
              this.#tasks.set(id, task);
            }
            if (
              this.isRunning &&
              isAsyncLoadableTask(existingTask) &&
              existingTask.loadable.isPaused
            ) {
              //replaced task was paused, so there must be a new top task to start
              //(even if it is the same loadable, it must be resumed)
              this.startTopTask(); //don't await
            }
          } //otherwise no need to replace because priority did not change
        } else if (!task.loadable.isLoaded) {
          //Add task to queue
          this.#tasks.set(id, task);
          this.#taskHeap.push(task);
        }
      } //skip tasks with loadables with falsey ids
    }

    //* Only one active processing of the queue can run at a time.
    //* If not started, it kicks of async startTopTask in background to process queue
    //  * Note: the queue can change while processing
    //* It is fine to call updateAndStart() to add more work to the queue while the queue is being processed
    //* If the queue is drained, and later more items are added to queue, the processing of the queue will restart
    if (!this.#taskHeapDrained) {
      if (this.#tasks.size > 0) {
        //start top task, but don't await it because the topTask may change before its done
        this.#taskHeapDrained = new WaitUntilSetController(this.signal);
        this.#lastPause = lastPause ?? performance.now();
        this.startTopTask();
        const nextPriority = this.peek()?.priority as number; //will be defined because size > 0
        const nextPriorityLevel = Math.floor(nextPriority);
        if (nextState.visibleBodies.length > 0 && nextPriorityLevel > 0) {
          this.afterEachPriorityLevel(
            this.#state,
            (nextPriorityLevel - 1) as P,
          );
        }
      }
    }
    //If this.#tasks.size > 0 || the previous this.startTopTask() was loading a sync task, then !this.#taskHeapDrained will be undefined
    //* Note: There is edge case where this.startTopTask() sets and delete this.#taskHeapDrained
    //  * This happens when using online metals app and stock is created/loaded with the sync .load() methods
    //  * So its import to re-test `!this.#taskHeapDrained` because even if this.#tasks.size > 0 above, it could be closed/removed
    if (!this.#taskHeapDrained) {
      this.afterEachPriorityLevel(this.#state); //all visibleIds (if any) already loaded
      return Promise.resolve(this.#lastPause || performance.now());
    }
    return this.#taskHeapDrained.waitUntilSet;
  }

  //peek(): looks at top item in priority queue
  //* doesn't need to be public because it is not used outside of LoadingPriorityQueue (but probably wouldn't hurt to be public)
  //  * Also see public this.currentlyLoadingId, which provides PoV of what is currently being processed
  //* if there are items at top of taskHeap that were marked for removal, or are done (and therefore should be removed), then they
  //  are filtered out. This filtering happens here because it is more performan compared to removing items sooner because the items
  //  may have been deeper in the queue and the queue only exposes top item.
  private peek(): TaskItem<T, P> | undefined {
    let peeked: TaskItem<T, P> | undefined;

    //Filter out items from taskHeap that were marked for removal until a pending TaskItem is peeked or heap is empty
    do {
      if (peeked) {
        //First delete previous peeked item because it was in toRemove
        this.#toRemove.delete(peeked);
        this.#taskHeap.pop();
        if (peeked.loadable.isLoaded) {
          //Should already be deleted, but delete just in case it exists
          this.#tasks.delete(peeked.loadable.id as string | number);
        }
      }
      peeked = this.#taskHeap.peek();
    } while (
      peeked &&
      (this.#toRemove.has(peeked) || peeked.loadable.isLoaded)
    );

    if (!peeked) {
      this.#taskHeapDrained?.set(this.#lastPause || performance.now());
      this.#taskHeapDrained = undefined;
    }

    return peeked;
  }

  //remove(): Call when done with a task, or when a task is being replaced because its priority changed
  private remove(task: TaskItem<T, P>): void {
    const peeked = this.peek(); //Peeks at first task in heap (Also removes removable tasks from top of queue)
    if (peeked) {
      const peekedId = peeked.loadable.id as string | number;
      const taskId = task.loadable.id as string | number;
      if (taskId === peekedId) {
        //Remove it now
        this.#toRemove.delete(this.#taskHeap.pop() as TaskItem<T, P>);
      } else {
        //Add it to toRemove so it can be removed from taskHeap in future
        this.#toRemove.add(task);
      }
      if (isAsyncLoadableTask(task) && task.loadable.isLoading) {
        task.loadable.pause();
      }
      this.#tasks.delete(taskId);
    }
  }

  private async startTopTask(): Promise<void> {
    //Start next task, but keep in mind, that it could be paused if the task's priority changes
    //* If/when a task's priority changes, this task will be paused and startTopTask should be
    //  called again to start the next highest priority task
    //* When this task completes, start the next top task
    const task = this.peek(); //currently topTask
    if (task) {
      if (isAsyncLoadableTask(task) && task.loadable.isPaused) {
        //Resume the task and return
        //* Note, because it already started in a previous call of startTopTask, the work to be done
        //  after it finishes is already setup. So it shouldn't be started again because then it would
        //  launch the next topTask multiple times when this task completes
        task.loadable.resume();
        return;
      }
      try {
        if (isAsyncLoadableTask(task)) {
          this.#lastPause = await task.loadable.loadAsync(this.#lastPause);
        } else {
          (task as TaskItem<SyncLoadable, P>).loadable.load();
        }
        this.afterEach(task.loadable);
      } catch (err) {
        //Handle by logging error and calling this.onFail() (It will also be removed in finally block.)
        console.error(
          `task loading error "${task.loadable.id}". It will be skipped`,
          err,
        );
        if (this.onFail) {
          this.onFail(task.loadable);
        }
      } finally {
        //Task is done, so remove it (it may or may not be first task in queue when done)
        this.remove(task);
        const currentPriorityLevel = Math.floor(task.priority) as P;
        const nextPriority = this.peek()?.priority;
        const nextPriorityLevel =
          nextPriority != null ? Math.floor(nextPriority) : null;
        if (currentPriorityLevel !== nextPriorityLevel) {
          //Finished loading tasks for a priority. Call this.afterEachPriority()
          this.afterEachPriorityLevel(this.#state as S, currentPriorityLevel);
        }

        //Start next task or signal task heap was drained
        if (this.size > 0 && !this.signal?.aborted) {
          //task just completed, start next top task
          this.startTopTask(); //don't await
        } else {
          this.#taskHeapDrained?.set(this.#lastPause || performance.now());
          this.#taskHeapDrained = undefined;
        }
      }
    }
  }
}
